import { Star, ExternalLink, PenLine } from "lucide-react";
import { GOOGLE_REVIEWS } from "@/data/content";
import FadeIn from "@/components/site/FadeIn";
import { Eyebrow } from "@/components/site/ui";
import { useLocale } from "@/i18n/LocaleContext";

const STAR_COUNT = 5;

// Fünf Sterne mit anteiliger Füllung (z. B. 4,8 → vier volle + 80 %)
const Stars = ({ value, size = "w-5 h-5", label, tag }) => (
  <span
    className="inline-flex items-center gap-0.5"
    role="img"
    aria-label={`${value.toLocaleString(tag)} ${label}`}
  >
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const fill = Math.max(0, Math.min(1, value - i));
      return (
        <span key={i} className={`relative ${size}`} aria-hidden="true">
          <Star className={`${size} absolute inset-0 text-star/30`} strokeWidth={1.5} />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
            <Star className={`${size} text-star fill-star`} strokeWidth={1.5} />
          </span>
        </span>
      );
    })}
  </span>
);

// Google-Wortmarke als Text, damit kein Bild von Google-Servern geladen wird
const GoogleWord = () => (
  <span className="font-600 tracking-tight" aria-label="Google">
    <span className="text-[#4285F4]">G</span>
    <span className="text-[#EA4335]">o</span>
    <span className="text-[#FBBC05]">o</span>
    <span className="text-[#4285F4]">g</span>
    <span className="text-[#34A853]">l</span>
    <span className="text-[#EA4335]">e</span>
  </span>
);

const formatMonth = (iso, tag) => {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(tag, { month: "long", year: "numeric" });
};

const ReviewCard = ({ review, t, tag }) => (
  <figure className="flex h-full flex-col rounded-2xl bg-white p-7 border border-border shadow-[0_8px_30px_rgb(44,51,51,0.06)]">
    <Stars value={review.rating} label={t.reviews.starsLabel} tag={tag} />
    <blockquote className="mt-4 flex-1 text-lg leading-relaxed text-ink">
      „{review.text}“
    </blockquote>
    <figcaption className="mt-5 flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-light font-serif text-lg font-600 text-brand">
        {review.name.charAt(0)}
      </span>
      <span className="leading-tight">
        <span className="block font-600 text-brand-dark">{review.name}</span>
        <span className="block text-sm text-muted-foreground">
          {formatMonth(review.date, tag)} · {t.reviews.on} <GoogleWord />
        </span>
      </span>
    </figcaption>
  </figure>
);

export default function GoogleReviews() {
  const { rating, count, asOf, profileUrl, writeUrl, items } = GOOGLE_REVIEWS;
  const { t, tag } = useLocale();
  const hasReviews = items.length > 0;

  return (
    <section className="bg-white" aria-labelledby="reviews-heading" data-testid="google-reviews">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div
          className={
            hasReviews
              ? "grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:items-start"
              : "max-w-2xl mx-auto text-center"
          }
        >
          {/* Zusammenfassung */}
          <FadeIn>
            <Eyebrow>{t.reviews.eyebrow}</Eyebrow>
            <h2 id="reviews-heading" className="mt-4 text-3xl md:text-4xl font-600 text-brand-dark leading-snug">
              {t.reviews.title}
            </h2>
            {!hasReviews && (
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                {t.reviews.emptyLead}
              </p>
            )}
            <div className={`mt-8 rounded-2xl bg-cream border border-border p-8 ${hasReviews ? "" : "text-start"}`}>
              <div className={`flex flex-wrap items-center gap-x-5 gap-y-3 ${hasReviews ? "" : "justify-center"}`}>
                <span className="font-serif text-6xl font-600 leading-none text-brand-dark">
                  {rating.toLocaleString(tag, { minimumFractionDigits: 1 })}
                </span>
                <span className="min-w-0">
                  <Stars value={rating} size="w-6 h-6" label={t.reviews.starsLabel} tag={tag} />
                  <span className="mt-1 block text-base text-muted-foreground whitespace-nowrap">
                    {count} {t.reviews.countOn} <GoogleWord />
                  </span>
                </span>
              </div>
              <p className="mt-6 text-base text-muted-foreground leading-relaxed">
                {t.reviews.source} {t.reviews.asOf} {asOf}.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-brand bg-white px-6 py-3.5 text-base font-600 text-brand transition-all duration-300 hover:bg-brand-light focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
                >
                  {t.reviews.all} <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </a>
                {writeUrl && (
                  <a
                    href={writeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3.5 text-base font-600 text-white transition-all duration-300 hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
                  >
                    <PenLine className="w-4 h-4" aria-hidden="true" /> {t.reviews.write}
                  </a>
                )}
              </div>
            </div>
          </FadeIn>

          {/* Einzelne Bewertungen */}
          {hasReviews ? (
            <div className="grid gap-6 sm:grid-cols-2">
              {items.slice(0, 4).map((r, i, arr) => (
                <FadeIn
                  key={`${r.name}-${r.date}`}
                  delay={0.1 + i * 0.08}
                  className={`h-full ${arr.length % 2 === 1 && i === arr.length - 1 ? "sm:col-span-2" : ""}`}
                >
                  <ReviewCard review={r} t={t} tag={tag} />
                </FadeIn>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
